import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { Table, TTableProps } from 'legacy/lut/library/list'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { IColumn } from 'core-components/library/list/types'
import { IFilter } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'

import { TranslationKeys } from './enums/TranslationKeys'
import { FilterFields } from './filterArea/configs'
import { FilterArea } from './filterArea/FilterArea'
import { getColumns } from './helpers/getColumns'
import { IJbpPartners, TSimpleTableFilterKeys } from './models'

import styles from './SimplePartnerTable.module.scss'

interface IProps
  extends Pick<TTableProps<IJbpPartners>, 'useRowCheckbox' | 'onCheck' | 'checkedValues'> {
  sourceKey: string
  onSelect?: (item: IJbpPartners) => Promise<void> | void
  onDelete?: (item: IJbpPartners) => void
  source?: Crud<IJbpPartners, string | number>
  bulkSave?: () => void
  bulkClear?: () => void
  withFilterArea?: boolean
  extraColumns?: IColumn[]
  baseColumns?: IColumn[]
  hideTableIfFiltersEmpty?: boolean
  usedFilters?: TSimpleTableFilterKeys[]
}

export const SimplePartnerTable = ({
  sourceKey,
  onSelect,
  onDelete,
  source,
  bulkClear,
  bulkSave,
  withFilterArea,
  extraColumns,
  hideTableIfFiltersEmpty,
  usedFilters,
  baseColumns,
  ...props
}: IProps) => {
  const crudSource = source || CrudStore.get(CrudStoreItem.WW_Partners, sourceKey)
  const editorColumns = useMemo(
    () => getColumns({ onDelete, onSelect, extraColumns, basePredefinedColumns: baseColumns }),
    [onDelete, onSelect, extraColumns, baseColumns]
  )

  const [isShowTable, setIsShowTable] = useState(!hideTableIfFiltersEmpty)

  const setShowTable = useCallback(() => {
    if (!hideTableIfFiltersEmpty) {
      setIsShowTable(true)
      return
    }
    const filters: IFilter[] = (crudSource.getFilter()?.filters as IFilter[]) || []
    const partnerId = filters.find((el) => el.field === FilterFields.PartnerId)?.value
    const partnerName = filters.find((el) => el.field === FilterFields.CommunicationName)?.value
    const market = filters.find((el) => el.field === FilterFields.GeoMarket)?.value
    const country = filters.find((el) => el.field === FilterFields.Country)?.value

    setIsShowTable(Boolean(partnerId || partnerName || country || market))
  }, [hideTableIfFiltersEmpty, crudSource])

  useEffect(() => {
    setShowTable()
    return crudSource.subscribe('change', () => {
      if (hideTableIfFiltersEmpty) {
        setShowTable()
      }
    })
  }, [crudSource, hideTableIfFiltersEmpty, setShowTable])

  return (
    <div>
      <RenderWhen condition={Boolean(withFilterArea)}>
        <FilterArea crudSource={crudSource} usedFilters={usedFilters} />
      </RenderWhen>

      <RenderWhen condition={props.checkedValues ? props.checkedValues.length > 0 : false}>
        <div className={styles.bulkButtons}>
          <Button className={styles.bulkButtons__btn} onClick={bulkSave}>
            <Translation text={TranslationKeys.bulkSave} />
          </Button>
          <Button color={'danger'} onClick={bulkClear}>
            <Translation text={TranslationKeys.bulkClear} />
          </Button>
        </div>
      </RenderWhen>

      <RenderWhen condition={isShowTable}>
        <Table
          {...props}
          pagination
          header
          columns={editorColumns}
          source={crudSource}
          hasTranslate
        />
      </RenderWhen>
    </div>
  )
}
